import { Injectable, inject } from '@angular/core';
import type { TimeSeriesIdentifierCategory } from '@clean-code/models';

import type {
  TimeSeries,
  TimeSeriesWithHistory,
} from '@clean-code/models';
import {
  type ID,
  type PagingResponse,
} from '@clean-code/models/common';
import { toDateOnly } from '@clean-code/shared/common';
import { GraphqlService, toGraphQLStringLiteral } from '@clean-code/shared/util-graphql';
import type { Dayjs } from 'dayjs';
import type { Observable } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class TimeSeriesPivotTableDataService {
  private graphqlService = inject(GraphqlService);
  private readonly entityFields = `
        key
        id
        openPrice
        highPrice
        lowPrice
        settlementPrice
        volume
        tradeDate
        unit
        isIntraday
        identifier
        name
        timestamp
        parserType
    `;

  getAll$(
    categoryId: ID,
    range: { start?: Date; end?: Date },
    areaMarketArea: ID | null,
    type: string | null,
  ): Observable<TimeSeries[]> {
    const formattedStartDate = this.formatDate(range.start);
    const formattedEndDate = this.formatDate(range.end);

    const typeFilter = type ? `type: ${type}` : '';

    const query = `
      query {
        controllingGetAll(
            categoryIdParam: ${categoryId}
            ${typeFilter}
            areaMarketAreaId: ${areaMarketArea},
            startDate: ${toGraphQLStringLiteral(`${formattedStartDate}T00:00:00Z`)},
            endDate: ${toGraphQLStringLiteral(`${formattedEndDate}T23:59:59Z`)}
          ){
              ${this.entityFields}
          }
      }
  `;
    return this.graphqlService.query<TimeSeries[]>(query);
  }

  marketAreaGetAll(categoryId: ID): Observable<ControllingMarketArea[]> {
    const query = `
      query {
        controllingMarketAreaAndTypeGetAll(
            categoryIdParam: ${categoryId}
          ){
                id
                name
          }
      }
  `;
    return this.graphqlService.query<ControllingMarketArea[]>(query);
  }

  private formatDate(date: Date): string {
    return date.toLocaleDateString('en-CA'); // en-CA gives YYYY-MM-DD format
  }

  public getAllDuplicatedData$(
    tradeDate: Dayjs,
    identifier: string,
  ): Observable<TimeSeries[]> {
    const query = `
      query {
        timeSeriesDuplicatedRecordData(tradeDate: ${toGraphQLStringLiteral(String(tradeDate))}, identifier: ${toGraphQLStringLiteral(identifier)}) {
          openPrice
          highPrice
          lowPrice
          volume
          settlementPrice
          identifier
          tradeDate
          unit
          timestamp
          isMainProduct
          id
          isIntraday
      }
      }
  `;
    return this.graphqlService.query<TimeSeries[]>(query);
  }

  deleteDuplicateData$(ids: ID[]): Observable<boolean> {
    const mutation = `
        mutation ($ids: [Int!]!){
          deleteTimeSeriesDuplicateRecord(ids: $ids)
        }`;

    return this.graphqlService.mutation<boolean>(mutation, { ids });
  }

  getById(id: string): Observable<TimeSeries> {
    const where = `where: { id: { eq: ${id} } }`;
    const query = `
      query {
          timeSeriesGetById(${where})
          {
              ${this.entityFields}
          }
      }
  `;
    return this.graphqlService.singleQuery<TimeSeries>(query);
  }

  getAllTimeSeriesIdentifierCategoriesLocalized$(): Observable<
    TimeSeriesIdentifierCategory[]
  > {
    const query = `
  query categoriesLocalized {
      timeSeriesIdentifierCategoryGetAllLocalized (
        order: {name: ASC}
      ) {
        id
        name
      }
    }
  `;

    return this.graphqlService.query<TimeSeriesIdentifierCategory[]>(query);
  }

  getAllControllingIdentifiers$(categoryId: ID): Observable<string[]> {
    const query = `
    query{
      controllingGetAllIdentifiers(categoryIdParam: ${categoryId})
    }
    `;
    return this.graphqlService.query<string[]>(query);
  }

  getAllControllingUnits$(): Observable<string[]> {
    const query = `
    query{
      controllingGetAllUnits
    }
    `;
    return this.graphqlService.query<string[]>(query);
  }

  checkIfCellDataExists$(
    identifier: string,
    tradeDate: Date,
  ): Observable<boolean> {
    const formattedTradeDate = toDateOnly(tradeDate);
    const query = `
      query{
        controllingCheckIfExists(identifier: ${toGraphQLStringLiteral(identifier)}, tradeDate: ${toGraphQLStringLiteral(`${formattedTradeDate}T00:00:00Z`)})
      }
    `;
    return this.graphqlService.query<boolean>(query);
  }

  public update$(
    idParam: ID,
    key: string,
    value: number | null,
    timestamp: Date | null = null,
  ): Observable<boolean> {
    const input = {
      id: idParam,
      value: value ? +value : null,
      fieldName: key,
      timeStamp: timestamp,
    };
    const mutation = `
        mutation ($input: UpdateTimeSeriesInput!){
          updateTimeSeries(input: $input)
        }`;
    return this.graphqlService.query<boolean>(mutation, { input });
  }

  public add$(
    key: string,
    value: number,
    date: Date,
    identifier: string,
    unit: string,
    timestamp: Date,
  ): Observable<boolean> {
    const input = {
      [key]: +value,
      tradeDate: date,
      identifier: identifier,
      unit: unit,
      timeStamp: timestamp,
    };
    const mutation = `
        mutation ($input: AddTimeSeriesInput!){
          addTimeSeries(input: $input)
        }`;
    return this.graphqlService.query<boolean>(mutation, { input });
  }

  public delete$(id: ID): Observable<boolean> {
    const mutation = `
        mutation ($id: Int!){
          deleteTimeSeries(id: $id)
        }`;
    return this.graphqlService.query<boolean>(mutation, { id });
  }

  public addHistory$(
    timeSeriesHistory: TimeSeriesHistoryAdd,
  ): Observable<boolean> {
    const input = {
      timeSeriesId: timeSeriesHistory.timeSeriesId,
      fieldName: timeSeriesHistory.fieldName,
      newValue: timeSeriesHistory.newValue,
    };

    const mutation = `
        mutation ($input: TimeSeriesHistoryAddInput!){
          addTimeSeriesHistory(input: $input)
        }`;

    return this.graphqlService.query<boolean>(mutation, { input });
  }

  public getTimeSeriesHistory$(
    id: ID,
    columnName: string,
  ): Observable<PagingResponse<TimeSeriesHistory>> {
    const query = `
    query{
      timeSeriesHistoryGetAll(where: { and: [{timeSeriesId: { eq: ${id} }}, {fieldName: {eq : ${toGraphQLStringLiteral(columnName)}}}] }, order: { updatedDate: DESC }) {
          totalCount
          items {
              timeSeriesId
              fieldName
              oldValue
              newValue
              updatedBy
              updatedByDec{
                fullName
              }
              updatedDate
              id
          }
      }
  }

    `;
    return this.graphqlService.query<PagingResponse<TimeSeriesHistory>>(query);
  }

  public getTimeSeriesIdsFromTimeSeriesHistory() {
    const query = `
    query{
      distinctTimeSeriesIdsGetAll(){
        timeSeriesId
        fieldName
      }
    }
    `;
    return this.graphqlService.query<TimeSeriesWithHistory[]>(query);
  }

  getDuplicateData() {
    const query = `
    query{
      timeSeriesDuplicatedRecordsGetAll(){
        tradeDate
        identifier
      }
    }
    `;
    return this.graphqlService.query<DuplicatedData[]>(query);
  }

  getTimeStamp$(identifier: string, date: Date): Observable<Date> {
    const query = `
      query{
        timeSeriesGetTimeStamp(identifier: ${toGraphQLStringLiteral(identifier)}, tradeDate: ${toGraphQLStringLiteral(toDateOnly(date))})
      }`;

    return this.graphqlService.query<Date>(query);
  }
}

export interface TimeSeriesIdentifierCategoryData {
  id: string;
  identifier: string;
}

export interface TimeSeriesHistory {
  id: ID;
  timeSeriesId: ID;
  fieldName: string;
  oldValue: number;
  newValue: number;
  updatedBy: string;
  updatedByDec: string;
  updatedDate: string;
}

export interface TimeSeriesHistoryAdd {
  timeSeriesId: ID;
  fieldName: string;
  newValue: number;
}

export interface ControllingMarketArea {
  id?: ID;
  name: string;
}

interface DuplicatedData {
  tradeDate: Date;
  identifier: string;
}
